import * as React from "react";
import { MoneyTextBoxEditor } from "../../../../../../../react-core/Html/Money/MoneyTextBoxEditor";
import { SelectEditor } from "../../../../../../../react-core/Html/SelectEditor";
import { TextBoxEditor } from "../../../../../../../react-core/Html/TextBoxEditor";
import { Numeric } from "../../../../../../../react-core/Html/Validation/RegExPatterns";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { Frequency } from "../../../../../../../Wrap/Constants/Enums/Domain/Frequency";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { DurationType } from "../../../../../../../Wrap/Constants/Enums/DurationType";
import { MoneyOrPercentageType } from "../../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { Money } from "../../../../../../../Wrap/Core/DataTypes/Money";
import { MoneyHelper } from "../../../../../../Shared/Common/MoneyHelper";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { SectionHeader } from "../../SharedComponents";
import { ChargesHelper } from "../../Helpers/ChargesHelper";

import { AdviserChargesContents, AdviserChargesValidation } from "../Resource/Contents";
import { FrequencyArraysMap, FrequencyTypeArrays } from "../../Resource/Contents";
import { WizardLinter, EWizardLinterStatus } from "../../../../../../Shared/Process/Process/WizardLinter";
import { EWizardLinterHelper } from "../../../NewBusinessWizard/Resource/EWizardLinterHelper";

interface IRIACProps extends IProcessComponentProps {
    type: SubAccountType;
}

interface IRIACState {}

export class RIAC extends ProcessComponent<IRIACProps, IRIACState> {
    constructor(props: any) {
        super(props);
        this.state = {};
    }

    componentDidMount() {
        super.componentDidMount();
        const { data, onChange } = this.props;
        if (data && data.RegularPaymentInitialAdviceCharge) {
            if (isNullOrEmpty(data.OngoingAdviceCharge.Frequency)) {
                this.onDataChange(
                    onChange,
                    { Frequency: Frequency.Monthly },
                    data,
                    "RegularPaymentInitialAdviceCharge"
                );
            }
        }
    }

    riacAmount(): Money {
        const { data } = this.props;
        const riac = data?.RegularPaymentInitialAdviceCharge;
        if (riac && riac.AdviceCharge) {return riac.AdviceCharge.Money;}
        return null;
    }

    riacAmountGiven(): boolean {
        const amount = this.riacAmount();
        return amount && amount.Amount > 0;
    }

    updateAmount(value: number) {
        const data = {
            AdviceCharge: {
                Type: MoneyOrPercentageType.Money,
                Money: { Amount: value, Currency: MoneyHelper.getDefaultCurrency() },
            },
        };
        return data;
    }

    updateDuration(value: number) {
        return {
            Duration: {
                DurationType: DurationType.NumberOfPayments,
                NumberOfPayments: value,
            },
        };
    }

    renderAlert() {
        const { data, type } = this.props;
        const riac = this.riacAmount();
        if (ChargesHelper.validateRIACAmount(data, type, riac.Amount)) {
            return (
                <Alert
                    alertType={AlertType.Warning}
                    message={AdviserChargesValidation.RIACEqualsOrGreaterThanRegularPayment}
                />
            );
        }
    }

    public render(): JSX.Element {
        const { data, onChange, type } = this.props;
        const riac = data?.RegularPaymentInitialAdviceCharge;
        if (!ChargesHelper.hasRegularPayments(data, type)) {
            return null;
        }
        return (
            <WizardLinter status={EWizardLinterStatus.PleaseCheck} enabled={EWizardLinterHelper.shouldDisplay(type)}>
                {SectionHeader(
                    AdviserChargesContents.RIAC,
                    "For monetary (£) Initial Adviser Charges – we try to take these straight away if there is cleared cash. For percentage (%) Initial Adviser Charges – we will pay this once the expected cash has been applied."
                )}
                {this.renderAlert()}
                <div className="row">
                    <div className="col-12 col-xs-6 col-sm-4">
                        <MoneyTextBoxEditor
                            label="Amount"
                            isNotForm={true}
                            //tooltip={AdviserChargesContents.RIACTooltip}
                            value={this.riacAmount()?.Amount}
                            onChange={(v) => {
                                this.onDataChange(
                                    onChange,
                                    this.updateAmount(v),
                                    data,
                                    "RegularPaymentInitialAdviceCharge"
                                );
                            }}
                            validationRules={[
                                {
                                    errorMessage: AdviserChargesValidation.CannotLessThanZero,
                                    validator: (v) => {
                                        return !v || v > 0;
                                    },
                                },
                            ]}
                            alwaysValidateOnUpdate={true}
                            currency={CurrencyCode.GBP}
                        />
                        <small className="mb-30 mt-4">
                            <strong>
                                £{(riac?.AdviceCharge.Money.Amount * riac?.Duration?.NumberOfPayments).toFixed(2)}
                            </strong>{" "}
                            total adviser charge
                        </small>
                    </div>

                    <div className="col-12 col-xs-6 col-sm-3">
                        <TextBoxEditor
                            label="Repeats"
                            cssWrapper="mb-0"
                            required={this.riacAmountGiven() ? "required" : null}
                            placeholder=""
                            value={riac?.Duration?.NumberOfPayments}
                            regex={{ errorMessage: "Numeric", value: Numeric }}
                            onChange={(v) => {
                                this.onDataChange(
                                    onChange,
                                    this.updateDuration(Number(v)),
                                    data,
                                    "RegularPaymentInitialAdviceCharge"
                                );
                            }}
                        />
                    </div>
                    <div className="col-12 col-xs-6 col-sm-3">
                        <SelectEditor
                            options={FrequencyTypeArrays}
                            getText={(v) => {
                                return FrequencyArraysMap[v];
                            }}
                            required="Please select frequency"
                            selected={riac ? riac.Frequency : Frequency.Monthly}
                            label="Deduction frequency"
                            onChange={(v) => {
                                this.onDataChange(
                                    onChange,
                                    { Frequency: v },
                                    data,
                                    "RegularPaymentInitialAdviceCharge"
                                );
                            }}
                        />
                    </div>
                </div>
                <hr className="mb-30" />
            </WizardLinter>
        );
    }
}
